import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { InfoIcon } from '@phosphor-icons/react'
import React, { useRef, useState } from 'react'

import { Icon } from '@/components/ui/icon'
import { theme } from '@/theme/theme'

import {
  TOOLTIP_DELAY_CLOSE,
  TOOLTIP_DELAY_OPEN,
  TOOLTIP_OFFSET,
  TOOLTIP_PADDING,
} from './tooltip.constants'
import * as S from './tooltip.styled'
import { TooltipProps } from './tooltip.types'

export const Tooltip = ({
  children,
  disabled = false,
  fullWidth,
  interactive = false,
  openOn = 'hover',
  trigger,
  triggerIcon = InfoIcon,
  placement = 'top',
  offsetValue = TOOLTIP_OFFSET,
  ...rest
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(offsetValue),
      flip(),
      shift({ padding: TOOLTIP_PADDING }),
      arrow({
        element: arrowRef,
      }),
    ],
    onOpenChange: setIsVisible,
    open: isVisible,
    placement,
    whileElementsMounted: autoUpdate,
  })

  const isClick = openOn === 'click'
  const click = useClick(context, { enabled: isClick })
  const hover = useHover(context, {
    delay: { close: TOOLTIP_DELAY_CLOSE, open: TOOLTIP_DELAY_OPEN },
    enabled: !isClick,
    handleClose: interactive ? safePolygon() : null,
  })
  const focus = useFocus(context, { enabled: !isClick })
  const dismiss = useDismiss(context, { ancestorScroll: true })
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  const defaultTrigger = (
    <S.TriggerButton
      ref={refs.setReference}
      aria-label="Show tooltip"
      type="button"
      {...getReferenceProps()}
    >
      <Icon icon={triggerIcon} size="2rem" />
    </S.TriggerButton>
  )

  return (
    <S.TooltipContainer
      {...rest}
      data-component-id="Tooltip"
      // Note: exposes the open state so a trigger that's only shown on hover can stay visible
      // while the pointer is over its (portaled) tooltip - e.g. the table header's actions.
      data-open={isVisible && !disabled ? 'true' : undefined}
    >
      {trigger ? (
        <S.TriggerWrapper ref={refs.setReference} $fullWidth={fullWidth} {...getReferenceProps()}>
          {trigger}
        </S.TriggerWrapper>
      ) : (
        defaultTrigger
      )}
      {isVisible && !disabled && (
        <FloatingPortal>
          <S.TooltipContent
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps({
              // Note: the content is portaled, but React still bubbles its events up the component
              // tree - a click here (e.g. selecting the text to copy it) would reach whatever wraps
              // the Tooltip, like a DropdownMenu trigger, and open it. A tooltip has nothing to
              // click, so keep its clicks to itself. Only `click`: pointer/mouse-down still bubble
              // for outside-press detection and text selection.
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            })}
          >
            <FloatingArrow ref={arrowRef} context={context} fill={theme.colors.black800} />
            <S.TooltipText>
              {typeof children === 'function' ? children(() => setIsVisible(false)) : children}
            </S.TooltipText>
          </S.TooltipContent>
        </FloatingPortal>
      )}
    </S.TooltipContainer>
  )
}
